import React, { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Grid,
  Typography,
  Alert,
} from '@mui/material';
import { TimeRecord } from '../types';
import { workerService } from '../services/workerService';

interface EditTimeRecordDialogProps {
  open: boolean;
  onClose: () => void;
  record: TimeRecord | null;
  onUpdate: () => void;
}

export const EditTimeRecordDialog: React.FC<EditTimeRecordDialogProps> = ({
  open,
  onClose,
  record,
  onUpdate,
}) => {
  const [formData, setFormData] = useState({
    mark_entry: '',
    lunch_entry: '',
    lunch_exit: '',
    final_exit: '',
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  React.useEffect(() => {
    if (record) {
      setFormData({
        mark_entry: record.mark_entry || '',
        lunch_entry: record.lunch_entry || '',
        lunch_exit: record.lunch_exit || '',
        final_exit: record.final_exit || '',
      });
    }
  }, [record]);

  const handleChange = (field: string) => (event: React.ChangeEvent<HTMLInputElement>) => {
    setFormData(prev => ({
      ...prev,
      [field]: event.target.value,
    }));
  };

  const handleSubmit = async () => {
    if (!record) return;

    setLoading(true);
    setError('');

    try {
      // Filtrar solo los campos que tienen valor
      const updateData: any = {};
      Object.entries(formData).forEach(([key, value]) => {
        if (value.trim() !== '') {
          updateData[key] = value;
        }
      });

      await workerService.updateTimeRecord(record.id, updateData);
      onUpdate();
      onClose();
    } catch (error: any) {
      setError(error.response?.data?.message || 'Error al actualizar el registro');
    } finally {
      setLoading(false);
    }
  };

  const formatTimeForInput = (time: string | null | undefined) => {
    if (!time) return '';
    return time.substring(0, 5); // HH:MM
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>
        Editar Registro de Tiempo
        {record && (
          <Typography variant="body2" color="textSecondary">
            {new Date(record.date).toLocaleDateString('es-ES', {
              weekday: 'long',
              year: 'numeric',
              month: 'long',
              day: 'numeric',
            })}
          </Typography>
        )}
      </DialogTitle>
      <DialogContent>
        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}
        
        <Grid container spacing={2} sx={{ mt: 1 }}>
          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              label="Hora de Entrada"
              type="time"
              value={formatTimeForInput(formData.mark_entry)}
              onChange={handleChange('mark_entry')}
              InputLabelProps={{ shrink: true }}
              helperText="Formato: HH:MM"
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              label="Inicio de Almuerzo"
              type="time"
              value={formatTimeForInput(formData.lunch_entry)}
              onChange={handleChange('lunch_entry')}
              InputLabelProps={{ shrink: true }}
              helperText="Formato: HH:MM"
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              label="Fin de Almuerzo"
              type="time"
              value={formatTimeForInput(formData.lunch_exit)}
              onChange={handleChange('lunch_exit')}
              InputLabelProps={{ shrink: true }}
              helperText="Formato: HH:MM"
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              label="Hora de Salida"
              type="time"
              value={formatTimeForInput(formData.final_exit)}
              onChange={handleChange('final_exit')}
              InputLabelProps={{ shrink: true }}
              helperText="Formato: HH:MM"
            />
          </Grid>
        </Grid>

        <Typography variant="body2" color="textSecondary" sx={{ mt: 2 }}>
          <strong>Nota:</strong> Deja vacío el campo que no quieras modificar. 
          Si modificas la hora de salida, el sistema recalculará automáticamente las horas trabajadas y el pago.
        </Typography>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={loading}>
          Cancelar
        </Button>
        <Button 
          onClick={handleSubmit} 
          variant="contained" 
          disabled={loading}
        >
          {loading ? 'Guardando...' : 'Guardar Cambios'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};
